import React, { createContext, useContext, useState } from "react";

export const useLocalFileActions = () => {
  const [tabs, setTabs] = useState([]); // Holds all open tabs with fileName, fileContent, originalContent, and state
  const [activeTab, setActiveTab] = useState(null); // The active tab object, including fileName, fileContent, originalContent, and state

  // Add a new tab with fileName, fileContent, originalContent, and an optional state ("synced" by default)
  const addTab = (
    fileName,
    fileContent,
    state = "synced",
    originalContent = null
  ) => {
    console.log("Adding tab:", fileName, fileContent, state);

    setTabs((prevTabs) => {
      // Avoid duplicates
      if (prevTabs.some((tab) => tab.fileName === fileName)) return prevTabs;

      // Limit to 5 tabs; remove the oldest if needed
      if (prevTabs.length >= 5) {
        return [
          ...prevTabs.slice(1), // Remove the oldest tab if limit is reached
          {
            fileName,
            fileContent,
            originalContent: originalContent ?? fileContent, // Use originalContent if provided
            state,
          },
        ];
      }

      // Add the new tab with the specified state
      return [
        ...prevTabs,
        {
          fileName,
          fileContent,
          originalContent: originalContent ?? fileContent, // Use originalContent if provided
          state,
        },
      ];
    });

    // Set the newly added tab as the active tab
    setActiveTab({
      fileName,
      fileContent,
      originalContent: originalContent ?? fileContent, // Use originalContent if provided
      state,
    });
  };
  // Update content of a specific tab by fileName and set the state to "update"
  const updateTabContent = (fileName, newContent) => {
    setTabs((prevTabs) =>
      prevTabs.map((tab) =>
        tab.fileName === fileName
          ? {
              ...tab,
              fileContent: newContent,
              state: tab.state === "new" ? "new" : "update", // Keep "new" if it was just added, otherwise mark as "update"
            }
          : tab
      )
    );

    if (activeTab?.fileName === fileName) {
      setActiveTab((prevActiveTab) => ({
        ...prevActiveTab,
        fileContent: newContent,
        state: prevActiveTab.state === "new" ? "new" : "update",
      }));
    }
  };

  // Close a tab based on its fileName
  const closeTab = (fileName) => {
    setTabs((prevTabs) => prevTabs.filter((tab) => tab.fileName !== fileName));

    if (fileName === activeTab?.fileName) {
      const remainingTabs = tabs.filter((tab) => tab.fileName !== fileName);
      setActiveTab(remainingTabs.length > 0 ? remainingTabs[0] : null);
    }
  };

  // Switch to another tab (based on fileName)
  const switchTab = (fileName) => {
    const tab = tabs.find((tab) => tab.fileName === fileName);
    if (tab) setActiveTab(tab);
  };

  // Optional: Reset a tab's state to "synced", e.g., after saving changes
  const resetTabState = (fileName) => {
    setTabs((prevTabs) =>
      prevTabs.map((tab) =>
        tab.fileName === fileName ? { ...tab, state: "synced" } : tab
      )
    );

    if (activeTab?.fileName === fileName) {
      setActiveTab((prevActiveTab) => ({
        ...prevActiveTab,
        state: "synced",
      }));
    }
  };

  return {
    tabs,
    activeTab,
    addTab,
    closeTab,
    switchTab,
    updateTabContent, // Expose updateTabContent to update a tab’s content
    resetTabState, // Expose resetTabState to reset a tab’s state to "synced"
  };
};

// Context for local file actions
const LocalFileActionsContext = createContext(null);

export const LocalFileActionsProvider = ({ children }) => {
  const localFileActions = useLocalFileActions();

  return (
    <LocalFileActionsContext.Provider value={localFileActions}>
      {children}
    </LocalFileActionsContext.Provider>
  );
};

// Custom hook for accessing the local file actions context
export const useLocalFileActionsContext = () => {
  const context = useContext(LocalFileActionsContext);
  if (!context) {
    throw new Error(
      "useLocalFileActionsContext must be used within a LocalFileActionsProvider"
    );
  }
  return context;
};
